import { useDealerDiscounts } from "app/hooks/checkout/useDealerDiscounts";
import {
  StandardProductFooterDataResultProps,
  useStandardProductFooterData,
} from "app/hooks/shared/standard-product/useStandardProductFooterData";
import React, { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import InformationIcon from "shared/ui/icons/Information";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import MonthlyPaymentModal from "widgets/shared/montly-payment-modal";
import { AmountSelector } from "./AmountSelector";
import { StandardProductFooterProps } from "./StandardProductOptionsFooterNormal";

export const StandardProductOptionsFooterMobile: React.FC<
  StandardProductFooterProps
> = ({ monthlyPayment, addStandardProductToCart }) => {
  const data = useStandardProductFooterData(monthlyPayment);

  const {
    isMonthlyPaymentModalOpened,
    setIsMonthlyPaymentModalOpened,
    showMonthlyPayment,
    standardProduct,
    standardProductName,
    standardProductPrice,
    changeAmount,
    amount,
  } = data as StandardProductFooterDataResultProps;

  // Dealer discounts, loading
  const { dealerDiscounts, isDealerDiscountsLoading } = useDealerDiscounts();

  const standardProductDiscounts = useMemo(() => {
    return dealerDiscounts?.find(
      (discount) => discount.productCode === standardProduct.productType.code,
    );
  }, [dealerDiscounts, standardProduct.productType.code]);

  const businessDiscount = standardProductDiscounts?.discount ?? 0;
  const businessDoorDiscountsPrice =
    standardProduct.price - (standardProduct.price * businessDiscount) / 100;

  const showDiscount =
    standardProductDiscounts &&
    standardProduct.price !== businessDoorDiscountsPrice;

  const savedAmount = (
    standardProduct.price - businessDoorDiscountsPrice
  ).toFixed(2);

  if (!data.standardProductName) {
    return null;
  }

  return (
    <>
      <MonthlyPaymentModal
        isOpen={isMonthlyPaymentModalOpened}
        setIsOpen={setIsMonthlyPaymentModalOpened}
      />
      <div className="gg-grid gg-h-36 gg-w-full gg-border-t-2 gg-border-gray-200 gg-bg-gray-50 gg-shadow-inner">
        {/* First row */}
        <div className="gg-flex gg-w-full gg-flex-row gg-justify-between gg-px-4 gg-pb-4 gg-pt-4 sm:gg-px-8">
          <div>
            {/* Selected standard product name */}
            <h1 className="gg-pr-2 gg-text-base gg-font-bold gg-text-slate-800 sm:gg-text-lg">
              {standardProductName}
            </h1>
          </div>
          <div className="gg-hidden md:gg-block">
            {/* Amount selector  */}
            <div className="gg-flex gg-pl-4 sm:gg-pl-8">
              <AmountSelector changeAmount={changeAmount} quantity={amount} />
            </div>
          </div>
          <div className="gg-flex gg-flex-col gg-space-y-1">
            <div className="gg-flex gg-flex-row">
              {/* Price formatted output */}
              {!showDiscount ? (
                <h1 className="gg-w-full gg-self-center gg-text-lg gg-font-bold gg-text-slate-800">
                  {standardProductPrice}
                </h1>
              ) : (
                <div className="gg-flex gg-flex-col">
                  {/* Price formatted output */}
                  <div className="gg-flex gg-items-center gg-gap-x-2">
                    <h4 className="gg-self-center gg-text-sm gg-font-bold gg-text-gray-400 gg-line-through">
                      {!isDealerDiscountsLoading ? (
                        standardProductPrice
                      ) : (
                        <Skeleton width={50} />
                      )}
                    </h4>
                  </div>
                  <h4 className="gg-text-lg gg-font-bold gg-text-red">
                    {!isDealerDiscountsLoading ? (
                      getFormattedPrice(businessDoorDiscountsPrice)
                    ) : (
                      <Skeleton width={90} />
                    )}
                  </h4>
                  <div className="gg-w-fit gg-bg-red gg-px-1">
                    <h4 className="gg-text-sm gg-font-bold gg-text-white">
                      {!isDealerDiscountsLoading ? (
                        <>Save €{savedAmount}</>
                      ) : (
                        <Skeleton width={95} />
                      )}
                    </h4>
                  </div>
                </div>
              )}
              {/* Monthly payment info icon */}
              {showMonthlyPayment && (
                <button
                  className="gg-pl-0.5"
                  onClick={() =>
                    setIsMonthlyPaymentModalOpened(!isMonthlyPaymentModalOpened)
                  }
                >
                  <InformationIcon
                    className="gg-fill-gray-500 hover:gg-fill-gray-900"
                    width={28}
                    height={28}
                  />
                </button>
              )}
            </div>

            {/* Monthly payment */}
            <h1
              className={`gg-text-sm gg-leading-5 gg-text-zinc-400 gg-transition-opacity gg-delay-150 gg-duration-300 gg-ease-in-out ${
                showMonthlyPayment ? "gg-opacity-100" : "gg-opacity-0"
              }`}
            >
              Of v.a. €{monthlyPayment} p/m
            </h1>
            {/* Monthly payment end */}
          </div>
        </div>
        {/* First row end */}

        {/* Second row  */}
        <div className="gg-flex gg-h-full gg-w-full gg-justify-between gg-border-t gg-border-gray-200">
          {/* Opties */}
          {/* <div className="gg-font-semibold gg-tracking-wide gg-text-sm gg-uppercase gg-text-zinc-400 gg-items-center gg-flex gg-py-3  gg-px-4 ">
					<h3>Opties</h3>
				</div> */}

          {/* Amount picker */}
          <div className="gg-flex gg-pl-4 sm:gg-pl-8 md:gg-hidden">
            <AmountSelector changeAmount={changeAmount} quantity={amount} />
          </div>

          {/* Amount picker end */}

          {/* Buttons  */}
          <div className="gg-flex gg-gap-0 gg-text-sm gg-leading-6 md:gg-w-full md:gg-justify-end">
            {/* Ontwerp */}
            {/* <button className="gg-font-black hover:gg-text-zinc-900  hover:gg-bg-gray-200 gg-justify-center gg-px-4 gg-py-3 gg-text-gray-500 gg-bg-gray-100 gg-border-l gg-border-gray-200 ">
              Maak ontwerp
            </button> */}

            {/* Add to cart button */}
            <button
              type="button"
              onClick={() => addStandardProductToCart()}
              data-testid="add-to-cart-mobile"
              className="gg-justify-center gg-rounded-sm gg-bg-yellow-300 gg-px-4 gg-py-3 gg-font-bold gg-text-dark-blue hover:gg-bg-yellow-400 hover:gg-text-zinc-900 sm:gg-px-8 md:gg-px-12"
            >
              In winkelmand
            </button>
            {/* Add to cart button end */}
          </div>
        </div>
        {/* Second row end */}
      </div>
    </>
  );
};
